import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Theme, styled } from '@mui/material/styles';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import TwDialog from '../../components/TwDialog/TwDialog';
import useOnScreen from '../../hooks/useOnScreen';
import { LOAD_TYPE, LoadRate, STOP_TYPE, TZ_USAGE } from '../../redux/api/load/load.model';
import LoadsService from '../../providers/Loads/Loads.service';
import { MeasurementSystem, PROVIDER, USER_ROLE } from '../../providers/User/User.model';
import DateUtils from '../../utils/dateUtils';
import { TwAutocompleteOption } from '../../components/TwAutocomplete/TwAutocomplete';
import StringUtils from '../../utils/stringUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import NumberUtils from '../../utils/numberUtils';
import TwNotifyDriver, {
  INotifyDriverWithFullSegment
} from '../../components/TwNotifyDriver/TwNotifyDriver';

import TwLoadForm from '../../components/TwLoadForm/TwLoadForm';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  openCreateTrip,
  setLoadDetailReload
} from '../../redux/features/navigation/navigationSlice';
import {
  DEFAULT_SEGMENT,
  getEmptyLoadRateServiceRow,
  OrderState,
  resetAdvancedRates,
  setLoadState,
  setOrdersForConsolidation,
  SliceOrderRate,
  SliceStop,
  SliceTrip,
  updateDriverPayConfirmationModal,
  updateInitialLoadRates,
  updateLoadRates,
  updateLoadRatesDisabled,
  updateQuickbooksEnabled,
  getMaxStopsCount
} from '../../redux/features/orders/ordersSlice';
import { nanoid } from '@reduxjs/toolkit';
import { RowData } from '../../components/TwAdvancedRates/RateRow';
import {
  InvoiceService,
  InvoiceServiceStatus,
  InvoiceServiceUnit,
  InvoiceSources
} from '../../redux/api/invoiceService/invoiceService.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import TwEditLoadInvoiceWarning from '../../components/TwEditLoadInvoiceWarning/TwEditLoadInvoiceWarning';
import { logRocketTrack } from '../../utils/logRocketUtils';
import {
  useGetInvoiceServicesQuery,
  useLazyGetInvoiceServicesQuery
} from '../../redux/api/invoiceService/invoiceServiceApi';
import QuickMenuItems from '../CreateOrder/QuickMenuItems/QuickMenuItems';
import Route from '../CreateOrder/Route/Route';
import OrderInfo from '../CreateOrder/OrderInfo/OrderInfo';
import DispatchInfo from '../CreateOrder/Dispatch/DispatchInfo';
import TwDispatchTripFromOrder from '../CreateOrder/Dispatch/TwDispatchTripFromOrder';
import AttachmentsInfo from '../CreateOrder/Attachments/AttachmentsInfo';
import useMediaQuery from '@mui/material/useMediaQuery';
import './EditLoad.scss';
import {
  BOOKING_TYPE,
  CLIENT_TYPE,
  ORDER_STATUS,
  Order,
  OrderCommodities,
  OrderRate,
  UpsertOrder
} from '../../redux/api/orders/orders.model';
import {
  StopStatus,
  Trip,
  TripData,
  TripDriver,
  TripDriverInfo,
  TRIP_STATUS,
  TripStop,
  TRIP_COVERAGE_STATUS,
  TripCommodities
} from '../../redux/api/trips/trips.model';
import {
  useCreateOrderMutation,
  useUpdateManyDocumentsMutation,
  useUpdateOrderMutation,
  useUploadOrderTripDocumentMutation
} from '../../redux/api/orders/ordersApi';
import { useGetTenantAccountingSettingsQuery } from '../../redux/api/tenant/tenantApi';
import TwAlert from '../../components/TwAlerts/TwAlerts';
import TwInfoModal from '../../components/TwInfoModal/TwInfoModal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ReactComponent as AlertIcon } from '../../assets/alert.svg';
import LoadUtils from '../../utils/loadUtils';
import TwWarningHelper from '../../components/TwWarningHelper/TwWarningHelper';
import {
  Validation,
  ValidationsResponse
} from '../../redux/api/validationsHelper/validationsHelper.model';
import { useValidateDispatchMutation } from '../../redux/api/validationsHelper/validationsApi';
import _ from 'lodash';

import { EntityType } from '../../redux/Global.model';
import DispatchHelperUtils from '../../utils/dispatchHelperUtils';
import Box from '@mui/material/Box';
import { formatStopsAndCommodities } from '../CreateTrip/TripOrderInfo/TripOrderInfo';
import {
  addOrderCommoditiesToTrip,
  addOrderStopsToTrip,
  addNewOrderCommoditiesToTrip
} from '../../redux/features/trip/tripSlice';
import { RateTableAttributes } from '../../redux/api/rateTable/rateTable.model';
import useCheckDriverPay from '../../hooks/useCheckDriverPay';
import TwDriverPayConfirmationModal from '../../components/TwDriverPayCalc/TwDriverPayConfirmationModal';
import { AmplitudeEvent } from '../../utils/amplitudeUtils';
import { logAmplitudeEvent } from '../../utils/amplitudeUtils';
import {
  generateNewOrderCommodity,
  mapCommodityListToOrderCommoditiesForSave,
  mapSavedOrderCommodityToCommodityList,
  mapPrefilledOrderCommodityToCommodityList
} from '../../utils/commodity/commodityUtils';
import TwLTLRatesWarning from './TwLTLRatesWarning';

export interface MenuItem {
  id: number;
  text: string;
  visible: boolean;
  ref: React.RefObject<HTMLDivElement>;
  icon: ReactNode;
}

interface EditDuplicateProps {
  loadId: string;
  open: boolean;
  showRateTable?: boolean;
  onClose?: (invoiceToEdit: string, duplicatedLoadId?: string) => void;
  onEditOrder?: (id?: string) => void;
}

const DataContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: 'transparent',
  paddingBottom: '50vh'
}));

export const INTERSECTION_OPTIONS: IntersectionObserverInit = {
  threshold: 0.33
};

const INITIAL_STATE = {
  isQboLearnMoreModalOpen: false,
  isDisable: false,
  isMainActionDisable: false,
  loading: true,
  componentReady: false,
  openNotify: false,
  originalDriversData: [] as Trip[],
  driversToNotify: [] as INotifyDriverWithFullSegment[],
  unassignedDriversToNotify: [] as INotifyDriverWithFullSegment[],
  loadNumber: '',
  createTripFromOrder: false,
  loadIdForNotifyDrivers: '',
  originalRate: 0,
  originalOrderRates: [] as OrderRate[],
  loadHasInvoiceSent: false,
  loadInvoiceSource: InvoiceSources.TB,
  loadInvoiceId: '',
  openInvoiceWarning: false,
  validations: [] as Validation[],
  openWarningHelper: false,
  openEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper: false,
  pickupDate: null as string | null,
  initialLoadResponse: undefined as Order | undefined
};

const EditDuplicateOrder: FC<EditDuplicateProps> = ({
  loadId,
  open,
  onClose,
  onEditOrder,
  showRateTable = false
}) => {
  const dispatch = useAppDispatch();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { addNotification } = useNotifications();
  const {
    enablePcMiler,
    enableWarningHelper,
    enableSkipDispatchModal,
    enableMaxNumberOfStops,
    enableCommoditiesDatabase
  } = useFeatureFlags();
  const anyDriverPayExists = useCheckDriverPay({ entity: EntityType.ORDER });
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const matchesAboveLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const {
    documents,
    duplicate_load,
    driver_pay_confirmation_modal,
    show_rates_warning_ltl,
    ...data
  } = useAppSelector((state) => ({
    ...state.orders,
    documents: state.orders.documents,
    duplicate_load: state.orders.duplicate_load,
    driver_pay_confirmation_modal: state.orders.driver_pay_confirmation_modal,
    show_rates_warning_ltl: state.orders.show_rates_warning_ltl
  }));
  const tripsFilterParams = useAppSelector((state) => state.activeTrips.filters);

  // --- State variables (all together) ---
  // Note the default values are set in the INITIAL_STATE object and they're all reset in the resetState function
  const [isQboLearnMoreModalOpen, setIsQboLearnMoreModalOpen] = useState<boolean>(
    INITIAL_STATE.isQboLearnMoreModalOpen
  );
  const [isDisable, setIsDisable] = useState<boolean>(INITIAL_STATE.isDisable);
  const [isMainActionDisable, setIsMainActionDisable] = useState<boolean>(
    INITIAL_STATE.isMainActionDisable
  );
  const [loading, setLoading] = useState<boolean>(INITIAL_STATE.loading);
  const [componentReady, setComponentReady] = useState<boolean>(INITIAL_STATE.componentReady);
  const [openNotify, setOpenNotify] = useState<boolean>(INITIAL_STATE.openNotify);
  const [originalDriversData, setOriginalDriversData] = useState<Trip[]>(
    INITIAL_STATE.originalDriversData
  );
  const [driversToNotify, setDriversToNotify] = useState<INotifyDriverWithFullSegment[]>(
    INITIAL_STATE.driversToNotify
  );
  const [unassignedDriversToNotify, setUnassignedDriversToNotify] = useState<
    INotifyDriverWithFullSegment[]
  >(INITIAL_STATE.unassignedDriversToNotify);
  const [loadNumber, setLoadNumber] = useState<string | number>(INITIAL_STATE.loadNumber);
  const [createTripFromOrder, setCreateTripFromOrder] = useState(INITIAL_STATE.createTripFromOrder);
  const [loadIdForNotifyDrivers, setLoadIdForNotifyDrivers] = useState<string>(
    INITIAL_STATE.loadIdForNotifyDrivers
  );
  const [originalRate, setOriginalRate] = useState<number>(INITIAL_STATE.originalRate);
  const [originalOrderRates, setOriginalOrderRates] = useState<OrderRate[]>(
    INITIAL_STATE.originalOrderRates
  );
  const [loadHasInvoiceSent, setLoadHasInvoiceSent] = useState<boolean>(
    INITIAL_STATE.loadHasInvoiceSent
  );
  const [loadInvoiceSource, setLoadInvoiceSource] = useState<InvoiceSources>(
    INITIAL_STATE.loadInvoiceSource
  );
  const [loadInvoiceId, setLoadInvoiceId] = useState<string>(INITIAL_STATE.loadInvoiceId);
  const [openInvoiceWarning, setOpenInvoiceWarning] = useState<boolean>(
    INITIAL_STATE.openInvoiceWarning
  );
  const [validations, setValidations] = useState<Validation[]>(INITIAL_STATE.validations);
  const [openWarningHelper, setOpenWarningHelper] = useState(INITIAL_STATE.openWarningHelper);
  const [
    openEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper,
    setOpenEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper
  ] = useState(INITIAL_STATE.openEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper);
  const [pickupDate, setPickupDate] = useState<string | null>(INITIAL_STATE.pickupDate);
  const [initialLoadResponse, setInitialLoadResponse] = useState<Order | undefined>(
    INITIAL_STATE.initialLoadResponse
  );
  // --- End state variables section ---

  const makeRateOptional = getTenantSetting('rate-optional');
  const isAutoCalculateMileageDisabled = getTenantSetting('disable-auto-calculate-mileage', false);
  const [triggerGetInvoiceServices] = useLazyGetInvoiceServicesQuery();

  const { data: divisionAccountingSettings } = useGetTenantAccountingSettingsQuery(
    {
      divisionId: data.selected_division || userData!.defaultDivision.id
    },
    {
      skip: !data.selected_division || !userData?.defaultDivision.id
    }
  );

  const quickbooksEnabled = !!divisionAccountingSettings?.division_qbo_credentials?.id;
  const defaultQBOInvoiceServiceId = divisionAccountingSettings?.default_qbo_invoice_service_id;

  const { data: invoiceServicesData = { content: [], total: 0 } } = useGetInvoiceServicesQuery({
    filters: {
      status: [InvoiceServiceStatus.ACTIVE, InvoiceServiceStatus.DISABLED],
      divisionId: data.selected_division || userData!.defaultDivision.id
    }
  });

  const formRef = useRef<HTMLFormElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const orderInfoRef = useRef<HTMLDivElement>(null);
  const routeInfoRef = useRef<HTMLDivElement>(null);
  const dispatchRef = useRef<HTMLDivElement>(null);
  const attachmentsInfoRef = useRef<HTMLDivElement>(null);
  const tripsRef = useRef(
    Array.from({ length: getMaxStopsCount(enableMaxNumberOfStops) }, () =>
      React.createRef<HTMLDivElement>()
    )
  );

  const tripsVisibilities = tripsRef.current.map((ref) => useOnScreen(ref, INTERSECTION_OPTIONS));

  const isOrderInfoVisible = useOnScreen(orderInfoRef, INTERSECTION_OPTIONS);
  const isRouteInfoVisible = useOnScreen(routeInfoRef, INTERSECTION_OPTIONS);
  const isDispatchInfoVisible = useOnScreen(dispatchRef, INTERSECTION_OPTIONS);
  const isAttachmentsVisible = useOnScreen(attachmentsInfoRef, INTERSECTION_OPTIONS);

  const [createOrder] = useCreateOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [updateManyDocuments] = useUpdateManyDocumentsMutation();
  const [uploadOrderTripDocument] = useUploadOrderTripDocumentMutation();
  const [validateDispatch] = useValidateDispatchMutation();

  // this is called when the component is closed to reset the state to the initial values (INITIAL_STATE)
  const resetState = (): void => {
    setIsQboLearnMoreModalOpen(INITIAL_STATE.isQboLearnMoreModalOpen);
    setIsDisable(INITIAL_STATE.isDisable);
    setIsMainActionDisable(INITIAL_STATE.isMainActionDisable);
    setLoading(INITIAL_STATE.loading);
    setComponentReady(INITIAL_STATE.componentReady);
    setOpenNotify(INITIAL_STATE.openNotify);
    setOriginalDriversData(INITIAL_STATE.originalDriversData);
    setDriversToNotify(INITIAL_STATE.driversToNotify);
    setUnassignedDriversToNotify(INITIAL_STATE.unassignedDriversToNotify);
    setLoadNumber(INITIAL_STATE.loadNumber);
    setCreateTripFromOrder(INITIAL_STATE.createTripFromOrder);
    setLoadIdForNotifyDrivers(INITIAL_STATE.loadIdForNotifyDrivers);
    setOriginalRate(INITIAL_STATE.originalRate);
    setOriginalOrderRates(INITIAL_STATE.originalOrderRates);
    setLoadHasInvoiceSent(INITIAL_STATE.loadHasInvoiceSent);
    setLoadInvoiceSource(INITIAL_STATE.loadInvoiceSource);
    setLoadInvoiceId(INITIAL_STATE.loadInvoiceId);
    setOpenInvoiceWarning(INITIAL_STATE.openInvoiceWarning);
    setValidations(INITIAL_STATE.validations);
    setOpenWarningHelper(INITIAL_STATE.openWarningHelper);
    setOpenEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper(
      INITIAL_STATE.openEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper
    );
    setPickupDate(INITIAL_STATE.pickupDate);
    setInitialLoadResponse(INITIAL_STATE.initialLoadResponse);
  };

  const createSegmentsAndStops = (
    segmentsFromResponse: Trip[],
    milage: number,
    stopsLtl?: TripStop[]
  ): {
    sliceSegments: SliceTrip[];
    sliceStops: SliceStop[];
    ltlTripsInOrder: Pick<SliceTrip, 'id' | 'readable_id' | 'selected_drivers'>[];
  } => {
    const sliceStops: SliceStop[] = [];
    let sliceSegments: SliceTrip[] = [];
    let providerStopsCurrentOrder = 1;
    const ltlTripsInOrder: Pick<SliceTrip, 'id' | 'readable_id' | 'selected_drivers'>[] = [];

    // FTL Tenant we review trips and stops and create the Slice shape
    if (!enableLtl) {
      // first we iterate over the segments and create the root slice stops
      segmentsFromResponse
        .sort((a, b) => a.order - b.order)
        .forEach((segment, segmentIndex) => {
          segment.stops.forEach((stop, stopIndex) => {
            const providerStop: SliceStop = {
              id: stop.id,
              segment_id: segment.id || '',
              ui_id: stop.id || nanoid(),
              order: stopIndex + 1,
              stop_identifier: stop.stop_identifier || '',
              provider_stop_identifier: duplicate_load ? '' : stop.stop_identifier || '',
              provider_edi_stop_identifier: duplicate_load
                ? undefined
                : stop.edi_stop_identifier || undefined,
              provider_date: duplicate_load
                ? null
                : (stop.start_date && DateUtils.getDateInLocale(new Date(stop.start_date))) || null,
              provider_location: stop.location
                ? ({
                    id: stop.location.id,
                    label: stop.location.name,
                    value: stop.location
                  } as TwAutocompleteOption)
                : null,
              location: stop.location,
              provider_stop_type: stop.stop_type
                ? ({
                    id: stop.stop_type,
                    label: StringUtils.getLoadIntermediateStopLabel(stop.stop_type),
                    value: stop.stop_type
                  } as TwAutocompleteOption)
                : null,
              provider_time_from: duplicate_load
                ? undefined
                : stop.start_time
                ? DateUtils.convertTimeToDate(stop.start_time)
                : undefined,
              provider_time_to: duplicate_load
                ? undefined
                : stop.end_time
                ? DateUtils.convertTimeToDate(stop.end_time)
                : undefined,
              provider_include_location_notes: !!stop.notes || !!stop.contact,
              notes: stop.notes || '',
              contact: stop.contact,
              provider_stop_status: stop.status || StopStatus.notStarted,
              provider_check_in_date:
                !duplicate_load && stop.in_time ? new Date(stop.in_time) : null,
              provider_check_in_time:
                !duplicate_load && stop.in_time ? new Date(stop.in_time) : null,
              provider_stop_in_time_overriden: !duplicate_load
                ? stop.is_in_time_overriden
                : undefined,
              provider_check_out_date:
                !duplicate_load && stop.out_time ? new Date(stop.out_time) : null,
              provider_check_out_time:
                !duplicate_load && stop.out_time ? new Date(stop.out_time) : null,
              provider_stop_out_time_overriden: !duplicate_load
                ? stop.is_out_time_overriden
                : undefined,
              is_shared_stop: stopIndex === 0 && segmentIndex > 0 // first stop of the segment that is not the first, is shared
            };

            sliceStops.push({
              ...providerStop,
              order: providerStopsCurrentOrder,
              sort_order: providerStopsCurrentOrder
            });
            if (!providerStop.is_shared_stop) {
              providerStopsCurrentOrder += 1;
            }
          });
        });

      // then we add those stops to the segments and create the slice segments
      sliceSegments = segmentsFromResponse
        .sort((a, b) => a.order - b.order)
        .map((segment, segmentIndex) => {
          const stopsFromSegment = sliceStops.filter((stop) => stop.segment_id === segment.id);
          const providerSegment = {
            ...segment,
            ui_id: segment.id || nanoid(),
            order: segmentIndex + 1,
            selected_carrier: duplicate_load ? undefined : segment.carrier_id || undefined,
            selected_owner_operator: duplicate_load
              ? undefined
              : segment.owner_operator_id || undefined,
            selected_trailer: duplicate_load ? undefined : segment.trailer_id || undefined,
            selected_truck: duplicate_load ? undefined : segment.truck_id || undefined,
            instructions: duplicate_load ? '' : segment.instructions || '',
            selected_drivers: duplicate_load
              ? [{ send_driver_link: false }]
              : segment.drivers.length > 0
              ? (segment.drivers.map((driver) => {
                  const loadedMiles =
                    driver.driver_pay_override_data?.loaded_miles ||
                    (segment.drivers.length > 1 ? NumberUtils.roundNumber(milage / 2) : milage);

                  const driverPayOverrideData = driver.driver_pay_override_data || null;

                  const driverData: TripDriverInfo = {
                    id: driver.id,
                    send_driver_link: false,
                    is_primary: driver.is_primary,
                    trip_driver_pay_other_payment: driver.trip_driver_pay_other_payment || []
                  };

                  if (driverPayOverrideData) {
                    driverData.driver_pay_override_data = {
                      ...driverPayOverrideData,
                      loaded_miles: loadedMiles || 0,
                      rate: driverPayOverrideData.rate || 0,
                      quantity: driverPayOverrideData.quantity || 0
                    };
                  }

                  if (driver.driver_pay_override) {
                    driverData.driver_pay_override = driver.driver_pay_override;
                  }

                  return {
                    ...driverData
                  };
                }) as Partial<TripDriverInfo>[])
              : [{ send_driver_link: false }],
            provider_stops: stopsFromSegment,
            empty_miles: duplicate_load ? null : segment.empty_miles,
            is_empty_miles_auto: duplicate_load ? false : segment.is_empty_miles_auto,
            estimated_mileage: duplicate_load ? null : segment.estimated_mileage,
            is_loaded_miles_auto: duplicate_load ? false : segment.is_loaded_miles_auto,
            automatic_mileage: duplicate_load ? null : segment.estimated_mileage,
            sort_order: segment.sort_order + 1, // BE sends sort_order starting from 0 but FE uses 1-based index,
            empty_mileage_source: !enablePcMiler ? PROVIDER.GOOGLE : segment.empty_mileage_source,
            loaded_mileage_source: !enablePcMiler ? PROVIDER.GOOGLE : segment.loaded_mileage_source
          };
          return providerSegment;
        });
    } else {
      /**
       * LTL is enabled, response does not return trips
       * only stops data, frontend components are expecting trips so we will create a fake trip
       * only to render data
       */
      const fakeTripId = nanoid();
      stopsLtl?.forEach((stop, stopIndex) => {
        const providerStop: SliceStop = {
          id: stop.id,
          segment_id: fakeTripId,
          ui_id: stop.id || nanoid(),
          order: stopIndex + 1,
          stop_identifier: stop.stop_identifier || '',
          provider_stop_identifier: duplicate_load ? '' : stop.stop_identifier || '',
          provider_edi_stop_identifier: duplicate_load
            ? undefined
            : stop.edi_stop_identifier || undefined,
          provider_date: duplicate_load
            ? null
            : (stop.start_date && DateUtils.getDateInLocale(new Date(stop.start_date))) || null,
          provider_location: stop.location
            ? ({
                id: stop.location.id,
                label: stop.location.name,
                value: stop.location
              } as TwAutocompleteOption)
            : null,
          location: stop.location,
          provider_stop_type: stop.stop_type
            ? ({
                id: stop.stop_type,
                label: StringUtils.getLoadIntermediateStopLabel(stop.stop_type),
                value: stop.stop_type
              } as TwAutocompleteOption)
            : null,
          provider_time_from: duplicate_load
            ? undefined
            : stop.start_time
            ? DateUtils.convertTimeToDate(stop.start_time)
            : undefined,
          provider_time_to: duplicate_load
            ? undefined
            : stop.end_time
            ? DateUtils.convertTimeToDate(stop.end_time)
            : undefined,
          provider_include_location_notes: !!stop.notes || !!stop.contact,
          notes: stop.notes || '',
          contact: stop.contact,
          provider_stop_status: stop.status || StopStatus.notStarted,
          provider_check_in_date: !duplicate_load && stop.in_time ? new Date(stop.in_time) : null,
          provider_check_in_time: !duplicate_load && stop.in_time ? new Date(stop.in_time) : null,
          provider_stop_in_time_overriden: !duplicate_load ? stop.is_in_time_overriden : undefined,
          provider_check_out_date:
            !duplicate_load && stop.out_time ? new Date(stop.out_time) : null,
          provider_check_out_time:
            !duplicate_load && stop.out_time ? new Date(stop.out_time) : null,
          provider_stop_out_time_overriden: !duplicate_load
            ? stop.is_out_time_overriden
            : undefined,
          is_shared_stop: false,
          coverage:
            !duplicate_load && stop.coverage ? stop.coverage : TRIP_COVERAGE_STATUS.UNCOVERED
        };

        sliceStops.push({
          ...providerStop,
          order: providerStopsCurrentOrder,
          sort_order: providerStopsCurrentOrder
        });
        if (!providerStop.is_shared_stop) {
          providerStopsCurrentOrder += 1;
        }
      });

      sliceSegments.push({
        ...DEFAULT_SEGMENT,
        id: fakeTripId,
        ui_id: fakeTripId,
        order: 1,
        provider_stops: sliceStops
      });
      // add trips from response to a specifi ltl trips state, used to check if driver pay in a trip exist
      // only care about shaping the drivers data on the trip.

      segmentsFromResponse.forEach((segment) => {
        ltlTripsInOrder.push({
          id: segment.id || nanoid(),
          readable_id: segment.readable_id || '',
          selected_drivers:
            segment.drivers.length > 0
              ? (segment.drivers.map((driver) => {
                  const loadedMiles =
                    driver.driver_pay_override_data?.loaded_miles ||
                    (segment.drivers.length > 1 ? NumberUtils.roundNumber(milage / 2) : milage);

                  const driverPayOverrideData = driver.driver_pay_override_data || null;

                  const driverData: TripDriverInfo = {
                    id: driver.id,
                    send_driver_link: false,
                    is_primary: driver.is_primary,
                    trip_driver_pay_other_payment: driver.trip_driver_pay_other_payment || [],
                    name: driver?.name || ''
                  };

                  if (driverPayOverrideData) {
                    driverData.driver_pay_override_data = {
                      ...driverPayOverrideData,
                      loaded_miles: loadedMiles || 0,
                      rate: driverPayOverrideData.rate || 0,
                      quantity: driverPayOverrideData.quantity || 0
                    };
                  }

                  if (driver.driver_pay_override) {
                    driverData.driver_pay_override = driver.driver_pay_override;
                  }

                  return {
                    ...driverData
                  };
                }) as Partial<TripDriverInfo>[])
              : [{ send_driver_link: false }]
        });
      });
    }

    return { sliceSegments, sliceStops, ltlTripsInOrder };
  };

  const transformOrderRates = (
    orderRates: Partial<OrderRate>[],
    orderRate: number | null | undefined,
    isQboEnabled: boolean,
    hideServiceName: boolean,
    serviceList?: InvoiceService[]
  ): {
    initialRates: RowData[];
    orderRates: SliceOrderRate[];
  } => {
    if (orderRates.length > 0) {
      const rates: SliceOrderRate[] = [];
      const initialRates: RowData[] = [];
      orderRates.forEach((orderRateRow) => {
        const findService = serviceList
          ? serviceList.find(
              (service) =>
                service.id ===
                (isQboEnabled ? orderRateRow.qbo_service_id : orderRateRow.invoice_service_id)
            )
          : invoiceServicesData.content.find(
              (service) =>
                service.id ===
                (isQboEnabled ? orderRateRow.qbo_service_id : orderRateRow.invoice_service_id)
            );
        const isServiceOk = !!findService;
        const rate: SliceOrderRate = {
          id: orderRateRow.id,
          ui_id: orderRateRow.id || nanoid(),
          invoice_service_id: isServiceOk ? orderRateRow.invoice_service_id : undefined,
          load_id: orderRateRow.load_id,
          quantity: orderRateRow.quantity,
          rate: orderRateRow.rate,
          service_name: isServiceOk ? orderRateRow.service_name : '',
          unit: orderRateRow.unit,
          sort_order: orderRateRow.sort_order,
          qbo_service_id: isServiceOk ? orderRateRow.qbo_service_id : undefined,
          qbo_service_type: orderRateRow.qbo_service_type,
          qbo_attributes: orderRateRow.qbo_attributes
        };
        rates.push(rate);
        initialRates.push({
          ...rate,
          initial_service_option: {
            id: isQboEnabled ? rate.qbo_service_id || '' : rate.invoice_service_id || '',
            label: hideServiceName ? '' : rate.service_name || '',
            value: {
              id: isQboEnabled ? rate.qbo_service_id || '' : rate.invoice_service_id || '',
              name: hideServiceName ? '' : rate.service_name || '',
              default_rate: 0,
              unit: rate.unit
            } as InvoiceService
          }
        });
      });
      return {
        initialRates,
        orderRates: rates
      };
    } else {
      // Could this happen? since at least one rate is required
      // if simple rate, quickboooks enabled and no default rate
      // show advanced rates after converting simple rate to advanced
      if (isQboEnabled && !defaultQBOInvoiceServiceId) {
        const initialRates = [
          {
            ...getEmptyLoadRateServiceRow(),
            rate: orderRate || 0
          }
        ];

        return {
          initialRates,
          orderRates: initialRates
        };
      }
    }

    return {
      initialRates: [getEmptyLoadRateServiceRow()],
      orderRates: [getEmptyLoadRateServiceRow()]
    };
  };

  const getLoad = useCallback(async () => {
    try {
      if (!loadId) return;

      const loadResponse = await LoadsService.getOrderById(loadId);
      // this help us to know if the division is connected to QBO currently for the first state
      // to make proper validations on the order rates saved because
      // they could be saved as TB services and then the division is connected to QBO
      const isOrderDivisionConnectedToQBO = loadResponse.is_division_connected_to_qbo;
      // this help us to get real services from the division liked to the order
      // beacuse the initial services are loaded using data.selected_division || userData!.defaultDivision.id
      // and data.selected_division on the first render is undefined due to this
      // userData!.defaultDivision.id is used to get the services and there is a mismatch
      // between the services and the order rates saved
      const initialServices = await triggerGetInvoiceServices({
        filters: {
          status: [InvoiceServiceStatus.ACTIVE, InvoiceServiceStatus.DISABLED],
          divisionId: loadResponse.division_id
        }
      });
      const initialServiceList = initialServices.data?.content || [];
      const sliceData = structuredClone(data);
      const stops = enableLtl ? loadResponse.stops : loadResponse.trips[0].stops;

      setLoadNumber(loadResponse?.readable_id);
      const isRateTableActive = showRateTable
        ? checkIfRateTableIsActive(loadResponse.rate_table, stops[0].start_date)
        : false;
      setInitialLoadResponse({
        ...loadResponse,
        is_rate_table_active: showRateTable ? isRateTableActive : undefined
      });

      sliceData.status = loadResponse.status || ORDER_STATUS.NOT_STARTED;
      sliceData.timezone_usage = loadResponse.tz_usage || TZ_USAGE.LOCAL;
      sliceData.selected_primary_client = loadResponse.primary_client;
      sliceData.customer_order_number = duplicate_load
        ? ''
        : loadResponse.customer_order_number || '';
      sliceData.provider_customer = loadResponse.raw_customer || undefined;
      sliceData.selected_customer = loadResponse.raw_customer?.id;

      sliceData.selected_dispatcher = duplicate_load
        ? undefined
        : loadResponse.dispatcher_id || undefined;
      sliceData.provider_contact = loadResponse.broker?.contact;
      sliceData.selected_contact = loadResponse.broker?.contact?.id;
      sliceData.selected_division = duplicate_load
        ? loadResponse.division_id
        : loadResponse.division_id;
      sliceData.provider_division = loadResponse.division;

      sliceData.existing_documents = duplicate_load ? [] : loadResponse.order_attachments;
      sliceData.send_order_update = !!loadResponse.order_status_notification_configuration;
      sliceData.order_notification_settings = loadResponse.order_status_notification_configuration;
      sliceData.order_type = loadResponse.order_type;
      sliceData.rate = loadResponse.rate || 0;
      sliceData.payable_amount = loadResponse.payable_amount
        ? loadResponse.payable_amount
        : undefined;
      sliceData.is_brokered_order = loadResponse.is_brokered_order;
      sliceData.invoice_division_id = loadResponse.invoice?.division_id || '';
      sliceData.qbo_attributes = loadResponse.qbo_attributes;
      sliceData.invoice = loadResponse.invoice;
      sliceData.instructions = duplicate_load ? undefined : loadResponse.instructions;
      sliceData.booking_type = loadResponse.booking_type;

      const loadPickupDate = stops[0].start_date || null;
      setPickupDate(loadPickupDate);

      const { initialRates, orderRates } = getRatesFromLoadResponse(
        loadResponse,
        duplicate_load,
        isOrderDivisionConnectedToQBO,
        initialServiceList
      );
      sliceData.initial_load_rates = initialRates;
      sliceData.order_rates = orderRates;
      sliceData.show_advanced_rates = true;
      if (showRateTable) {
        sliceData.rate_table_id = loadResponse.rate_table_id;
        sliceData.is_rate_table_active = isRateTableActive;
        sliceData.is_rate_table_deleted = loadResponse.is_rate_table_deleted;
      }

      if (loadResponse.order_commodities && loadResponse.order_commodities.length > 0) {
        sliceData.order_commodities = loadResponse.order_commodities.map((commodity) => {
          return {
            id: commodity.id,
            ui_uid: commodity.id || nanoid(),
            description: commodity.description || '',
            quantity: commodity.quantity || null,
            weight: commodity.weight || null,
            packaging_unit: commodity.packaging_unit || null
          };
        });
        // if commodities database is enabled, we need to map the commodities to the commodity list
        // otherwise, we need to use the commodities from the load response
        if (enableCommoditiesDatabase) {
          sliceData.new_order_commodities = loadResponse.order_commodities.map((commodity) =>
            mapSavedOrderCommodityToCommodityList(commodity)
          );
        }
      } else {
        sliceData.order_commodities = [];
        // if commodities database is enabled, we need to declare the new order commodities as empty
        if (enableCommoditiesDatabase) {
          sliceData.new_order_commodities = [generateNewOrderCommodity(MeasurementSystem.IMPERIAL)];
        }
      }

      const { sliceSegments, sliceStops, ltlTripsInOrder } = createSegmentsAndStops(
        loadResponse.trips as Trip[],
        loadResponse.estimated_mileage || 0,
        loadResponse.stops || []
      );
      sliceData.provider_stops = sliceStops;
      sliceData.trips = sliceSegments;
      // in duplicate mode, we don't want to show keep track of the trips associated to the order
      // because we are creating a new order
      sliceData.ltl_trips_in_order = duplicate_load ? [] : ltlTripsInOrder || [];
      sliceData.estimated_mileage = loadResponse.estimated_mileage || 0;
      sliceData.total_automatic_mileage = loadResponse.estimated_mileage || 0;

      // save location ids for this load to be used for checking location association
      const locationIds: string[] = [];
      loadResponse.trips.forEach((segment) => {
        segment.stops.forEach((stop) => {
          if (stop.location_id) {
            locationIds.push(stop.location_id);
          }
        });
      });
      sliceData.initial_location_ids = locationIds;

      setOriginalRate(loadResponse.rate || 0);
      setOriginalOrderRates(loadResponse.order_rates || []);
      setLoadHasInvoiceSent(!!loadResponse.invoice?.sent);
      setLoadInvoiceSource(loadResponse.invoice?.source || InvoiceSources.TB);
      !!loadResponse.invoice?.sent &&
        loadResponse.invoice.id &&
        setLoadInvoiceId(loadResponse.invoice.id);
      setOriginalDriversData(loadResponse.trips);
      dispatch(setLoadState(sliceData));
      setLoadIdForNotifyDrivers(loadId);
    } catch (err) {
      console.error(err);
      addNotification({
        message: "Couldn't get order info.",
        variant: 'error'
      });
    }
  }, [loadId, duplicate_load, showRateTable]);

  const getRatesFromLoadResponse = (
    orderResponse: Order,
    isDuplicateMode: boolean,
    isDivQboConnected?: boolean,
    serviceList?: InvoiceService[]
  ): {
    initialRates: RowData[];
    orderRates: SliceOrderRate[];
    isOrderRateTableActive?: boolean;
    isDivQboConnected?: boolean;
  } => {
    if (!showRateTable) {
      const { order_rates, rate } = orderResponse;
      const { initialRates, orderRates } = transformOrderRates(
        order_rates || [],
        rate,
        isDivQboConnected || quickbooksEnabled,
        false,
        serviceList
      );
      return {
        initialRates,
        orderRates
      };
    }

    const { rate_table_id, rate_table, is_rate_table_deleted, order_rates, rate } = orderResponse;
    const stops = enableLtl ? orderResponse.stops : orderResponse.trips[0].stops;
    const orderPickupDate = stops[0].start_date || null;
    const { initialRates, orderRates } = transformOrderRates(
      order_rates || [],
      rate,
      isDivQboConnected || quickbooksEnabled,
      false,
      serviceList
    );
    if (rate_table_id && rate_table) {
      const isRateTableActive = checkIfRateTableIsActive(rate_table, orderPickupDate);
      // Only when rate table is deleted or inactive and in duplicate mode
      // return empty rates
      if ((is_rate_table_deleted || !isRateTableActive) && isDuplicateMode) {
        return {
          initialRates: [getEmptyLoadRateServiceRow()],
          orderRates: [getEmptyLoadRateServiceRow()],
          isOrderRateTableActive: false
        };
      } else {
        return {
          initialRates,
          orderRates,
          isOrderRateTableActive: isRateTableActive
        };
      }
    } else {
      return {
        initialRates,
        orderRates
      };
    }
  };

  const getRatesFromOrderState = (
    orderState: OrderState | Order,
    isQboEnabled: boolean
  ): {
    initialRates: RowData[];
    orderRates: SliceOrderRate[];
  } => {
    const { order_rates, rate } = orderState;
    if (order_rates && order_rates.length > 0) {
      // this is the case when we have rates created using different source
      // e.g. order rates using TB services and then user enables QBO for the division
      // or on edit order, user changes the division and the new division has QBO enabled
      // this clears the service name in order to display the box as empty
      const areQboRates = order_rates.some((rate) => !!rate.qbo_service_id);
      // hide service name if rates are from different sources
      // if order rates are from TB invoice services and QBO is enabled
      // or if order rates are from QBO invoice services and QBO is disabled
      const hideServiceName = (isQboEnabled && !areQboRates) || (!isQboEnabled && areQboRates);

      const { initialRates, orderRates } = transformOrderRates(
        order_rates,
        rate,
        isQboEnabled,
        hideServiceName
      );
      return {
        initialRates,
        orderRates
      };
    }

    return {
      initialRates: [],
      orderRates: []
    };
  };

  const handleClose = (openInvoice?: boolean): void => {
    setLoading(true);
    setInitialLoadResponse(undefined);
    // in the case of duplicate load we want to show the new load detail overlay

    onClose &&
      onClose(
        openInvoice && loadInvoiceId ? loadInvoiceId : '',
        duplicate_load ? loadIdForNotifyDrivers : undefined
      );
  };

  const findDiffDriversDuplicateLoad = (
    original: Trip[],
    incoming: Trip[],
    orderReadableId: number
  ): {
    fullSegment: Trip;
    drivers: TripDriver[];
  }[] => {
    const diff: {
      fullSegment: Trip;
      drivers: TripDriver[];
    }[] = [];

    incoming
      .filter((segment) => segment.drivers.length > 0)
      .forEach((segment) => {
        diff.push({
          fullSegment: { ...segment, order_readable_id: orderReadableId },
          drivers: segment.drivers
        });
      });

    return diff;
  };

  const findDiffDriversEditLoad = (
    original: Trip[],
    incoming: Trip[],
    orderReadableId: number
  ): INotifyDriverWithFullSegment[] => {
    const diff: INotifyDriverWithFullSegment[] = [];

    incoming.forEach((incomingSegment, segmentIndex) => {
      const originalSegment = original[segmentIndex];

      // Check if any load stops have changed in the segment
      const areLoadStopsChanged =
        !originalSegment ||
        incomingSegment.stops.some((incomingLoadStop, loadStopIndex) => {
          const originalLoadStop = originalSegment.stops[loadStopIndex];
          return (
            !originalLoadStop ||
            incomingLoadStop.location_id !== originalLoadStop.location_id ||
            incomingLoadStop.start_time !== originalLoadStop.start_time ||
            incomingLoadStop.end_time !== originalLoadStop.end_time ||
            incomingLoadStop.start_date !== originalLoadStop.start_date ||
            incomingLoadStop.end_date !== originalLoadStop.end_date
          );
        });

      incomingSegment.drivers.forEach((incomingDriver, driverIndex) => {
        const isDriverAdded =
          !originalSegment ||
          !originalSegment.drivers.some(
            (originalDriver) => originalDriver.id === incomingDriver.id
          );

        if (isDriverAdded || areLoadStopsChanged) {
          const diffDriver: INotifyDriverWithFullSegment = {
            driverId: incomingDriver.id,
            driverIndex,
            fullSegment: { ...incomingSegment, order_readable_id: orderReadableId },
            isForExistingLoadUpdate: !isDriverAdded
          };

          diff.push(diffDriver);
        }
      });
    });

    return diff;
  };

  const findUnassignedDrivers = (
    original: Trip[],
    incoming: Trip[],
    orderReadableId: number
  ): INotifyDriverWithFullSegment[] => {
    const unassignedDrivers: INotifyDriverWithFullSegment[] = [];

    original.forEach((orig, segmentIndex) => {
      const incomingSegment = incoming[segmentIndex];

      orig.drivers.forEach((originalDriver, driverIndex) => {
        const isDriverAssigned =
          !!incomingSegment &&
          incomingSegment.drivers.some((incomingDriver) => incomingDriver.id === originalDriver.id);

        if (!incomingSegment || !isDriverAssigned) {
          const unassignedDriver: INotifyDriverWithFullSegment = {
            driverId: originalDriver.id,
            driverIndex: driverIndex,
            fullSegment: { ...orig, order_readable_id: orderReadableId },
            deleted_trip_index: segmentIndex
          };

          unassignedDrivers.push(unassignedDriver);
        }
      });
    });

    return unassignedDrivers;
  };

  const handleDuplicateLoad = async (ignoreAndProceed?: boolean): Promise<void> => {
    setLoading(true);
    const loadData: UpsertOrder = {
      status: ORDER_STATUS.NOT_STARTED, // always set to draft when duplicating
      rate: data.rate ? data.rate : 0,
      is_brokered_order: data.is_brokered_order,
      payable_amount: data.payable_amount,
      tz_usage: data.timezone_usage ? data.timezone_usage : TZ_USAGE.LOCAL,
      primary_client: data.selected_primary_client || CLIENT_TYPE.BROKER,
      customer_order_number: data.customer_order_number,
      dispatcher_id: data.selected_dispatcher || undefined,
      attachmentList: data.attachments,
      send_order_update: data.send_order_update,
      configurations:
        // Re-map config to not send unnecessary load id or config id
        data.send_order_update && data.order_notification_settings
          ? {
              recipients: data.order_notification_settings?.recipients,
              order_status_events: data.order_notification_settings?.order_status_events,
              stop_status_events: data.order_notification_settings?.stop_status_events,
              stop_types: data.order_notification_settings?.stop_types
            }
          : null,
      order_type: data.order_type,
      trips: [],
      division_id: data.selected_division || userData?.defaultDivision.id,
      order_commodities: [],
      booking_type: enableLtl ? data.booking_type : BOOKING_TYPE.FTL,
      instructions: data.instructions,
      rate_table_id: data.rate_table_id || undefined
    };

    if (data.show_advanced_rates) {
      const loadRates: LoadRate[] = [];

      [...data.order_rates]
        .filter((lr) => !!lr.unit)
        .sort(
          (r1, r2) =>
            (r1.sort_order || data.order_rates.length) - (r2.sort_order || data.order_rates.length)
        )
        .forEach((lr, index) => {
          const unit = lr.unit || InvoiceServiceUnit.FLAT;

          loadRates.push({
            invoice_service_id: lr.qbo_service_id ? undefined : lr.invoice_service_id,
            qbo_service_id: lr.qbo_service_id ? lr.qbo_service_id : undefined,
            qbo_service_type: lr.qbo_service_type ? lr.qbo_service_type : undefined,
            service_name: lr.service_name,
            quantity: unit === InvoiceServiceUnit.FLAT ? 1 : lr.quantity || 0,
            rate: lr.rate || 0,
            unit,
            sort_order: index + 1,
            qbo_attributes: lr.qbo_attributes
          });
        });

      loadData.order_rates = loadRates;
      loadData.rate = undefined;
    } else {
      loadData.order_rates = LoadUtils.createDefaultAdvancedRate(
        data.rate,
        quickbooksEnabled,
        invoiceServicesData?.content,
        makeRateOptional
      );
      loadData.rate = undefined;
    }

    // load commodities check
    if (enableCommoditiesDatabase) {
      const commodities =
        data.new_order_commodities?.map((c) => {
          return mapCommodityListToOrderCommoditiesForSave(c, true);
        }) ?? [];
      loadData.order_commodities = commodities;
    } else {
      const newCommodities: OrderCommodities[] = [];
      data.order_commodities.forEach((commodity) => {
        if (commodity.description === '' && !commodity.quantity && !commodity.weight) {
          return;
        } else {
          // get commodity data without the id field
          const { id: id, ...otherCommodityData } = commodity;

          const comm = {
            ...otherCommodityData,
            quantity: commodity.quantity || 0,
            weight: commodity.weight || 0
          };
          newCommodities.push(comm);
        }
      });

      if (newCommodities.length > 0) {
        loadData.order_commodities = newCommodities;
      }
    }

    data.selected_customer ? (loadData.broker_id = data.selected_customer) : null;
    data.selected_contact ? (loadData.customer_contact_id = data.selected_contact) : null;

    data.selected_dispatcher ? (loadData.dispatcher_id = data.selected_dispatcher) : null;

    const providerSegments = data.trips;
    const newLoadSegments: TripData[] = [];

    // LTL is enabled only stops are sent
    if (enableLtl) {
      const err = LoadUtils.validateStopIdentifiers(data.provider_stops);
      if (err) {
        setLoading(false);
        addNotification({
          message: err,
          variant: 'error'
        });
        return;
      }

      loadData.stops = data.provider_stops.map((s) => {
        const stop: TripStop = {
          status: StopStatus.notStarted, // duplicates should be reset
          order: s.order,
          sort_order: s.order,
          start_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          start_time: s.provider_time_from
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_from))
            : undefined,
          end_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          end_time: s.provider_time_to
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_to))
            : undefined,
          stop_type: (s.provider_stop_type?.id as STOP_TYPE) || undefined,
          stop_identifier:
            s.provider_stop_type?.id && s.provider_stop_identifier
              ? s.provider_stop_identifier
              : undefined,
          notes: s.provider_include_location_notes ? s.notes : undefined,
          contact: s.provider_include_location_notes ? s.contact : undefined
        };

        if (s.provider_location?.id) {
          stop.location_id = s.provider_location.id;
        }

        return stop;
      });
      delete loadData['trips'];
    } else {
      // FTL is enabled, trips and stops are sent
      for (const providerSegment of providerSegments) {
        const newSegment: TripData = {
          order: providerSegment.order,
          carrier_id: providerSegment.selected_carrier || undefined,
          instructions: providerSegment.instructions,
          trailer_id: providerSegment.selected_trailer || undefined,
          truck_id: providerSegment.selected_truck || undefined,
          empty_miles: providerSegment.empty_miles,
          estimated_mileage: providerSegment.estimated_mileage,
          pickup_number: providerSegment.pickup_number,
          status: TRIP_STATUS.DRAFT, // duplicates should be reset
          stops: [],
          is_empty_miles_auto: providerSegment.is_empty_miles_auto,
          is_loaded_miles_auto: providerSegment.is_loaded_miles_auto,
          empty_mileage_source: providerSegment.empty_mileage_source,
          loaded_mileage_source: providerSegment.loaded_mileage_source
        };

        providerSegment.selected_trailer
          ? (newSegment.trailer_id = providerSegment.selected_trailer)
          : null;
        providerSegment.selected_truck
          ? (newSegment.truck_id = providerSegment.selected_truck)
          : null;
        providerSegment.selected_carrier
          ? (newSegment.carrier_id = providerSegment.selected_carrier)
          : null;
        providerSegment.selected_owner_operator
          ? (newSegment.owner_operator_id = providerSegment.selected_owner_operator)
          : null;

        // Save drivers to segment
        const driversToCreate = providerSegment.selected_drivers.filter((d) => !!d.id);

        driversToCreate.length > 0
          ? (newSegment.drivers = driversToCreate as TripDriverInfo[])
          : undefined;

        const err = LoadUtils.validateStopIdentifiers(
          data.order_type === LOAD_TYPE.SIMPLE
            ? data.provider_stops
            : providerSegment.provider_stops
        );
        if (err) {
          setLoading(false);
          addNotification({
            message: err,
            variant: 'error'
          });
          return;
        }

        // Build stops data
        newSegment.stops = (
          data.order_type === LOAD_TYPE.SIMPLE
            ? data.provider_stops
            : providerSegment.provider_stops
        ).map((s) => {
          const stop: TripStop = {
            status: StopStatus.notStarted, // duplicates should be reset
            order: s.order,
            sort_order: s.order,
            start_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
            start_time: s.provider_time_from
              ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_from))
              : undefined,
            end_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
            end_time: s.provider_time_to
              ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_to))
              : undefined,
            stop_type: (s.provider_stop_type?.id as STOP_TYPE) || undefined,
            stop_identifier:
              s.provider_stop_type?.id && s.provider_stop_identifier
                ? s.provider_stop_identifier
                : undefined,
            notes: s.provider_include_location_notes ? s.notes : undefined,
            contact: s.provider_include_location_notes ? s.contact : undefined
          };

          if (s.provider_location?.id) {
            stop.location_id = s.provider_location.id;
          }

          return stop;
        });
        newLoadSegments.push(newSegment);
      }

      // Add segments to load data
      loadData.trips = newLoadSegments;
    }

    loadData.attachmentList = data.existing_documents; // backend will receive too an array with the objects

    let driversDifference: { fullSegment: Trip; drivers: TripDriver[] }[] = [];

    setIsDisable(true);

    let validationsResponse: ValidationsResponse | null = null;
    let hasValidations = false;
    if (enableWarningHelper && !ignoreAndProceed) {
      try {
        validationsResponse = await validateDispatch(
          DispatchHelperUtils.getValidateDispatchPayload(loadData, enableLtl)
        ).unwrap();
      } catch (error) {
        console.error(error);
      }
    }

    if (validationsResponse) {
      hasValidations = validationsResponse.data.length > 0;

      // check if there are new validations
      const hasNewValidations = !_.isEqual(validationsResponse.data, validations);

      if (!hasNewValidations) {
        handleIgnoreAndProceed();

        return;
      }

      if (!hasValidations) {
        handleIgnoreAndProceed();

        return;
      }

      setOpenWarningHelper(hasValidations);
      setValidations(validationsResponse.data);
    }

    if (!hasValidations || ignoreAndProceed || !enableWarningHelper) {
      createOrder({ data: loadData })
        .unwrap()
        .then(async (newLoad) => {
          logAmplitudeEvent(AmplitudeEvent.NEW_ORDER_DUPLICATED);
          if (documents && documents?.length > 0) {
            await Promise.all(
              documents.map((doc) =>
                uploadOrderTripDocument({
                  document: {
                    ...doc,
                    orderId: newLoad.id
                  },
                  reviewed: true
                }).unwrap()
              )
            ).catch((error) => {
              logRocketTrack('Upload', {
                action: 'Upload document on edit load',
                severity: 'high',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              console.error('Error upload document', error);
              addNotification({
                message: 'Error uploading document(s)',
                variant: 'error'
              });
            });
          }
          return newLoad;
        })
        .then((newLoad) => {
          setLoadIdForNotifyDrivers(newLoad.id);

          driversDifference = findDiffDriversDuplicateLoad([], newLoad.trips, newLoad.readable_id);

          // Returns driver's that are new or changed, in this case all are new
          if (!enableSkipDispatchModal && driversDifference.length > 0) {
            const updatedArrayDrivers: INotifyDriverWithFullSegment[] = [];

            driversDifference.forEach((segment: { fullSegment: Trip; drivers: TripDriver[] }) => {
              segment.drivers.forEach((driver: TripDriver, index: number) => {
                const selectedDriver = {
                  driverId: driver.id,
                  fullSegment: segment.fullSegment,
                  driverIndex: index,
                  isForExistingLoadUpdate: false
                };
                updatedArrayDrivers.push(selectedDriver);
              });
            });

            setDriversToNotify(updatedArrayDrivers);
            setOpenNotify(true);
          } else if (enableLtl && createTripFromOrder) {
            onClose && onClose('');
            setInitialLoadResponse(undefined);
            consolidateInTripFromOrder(newLoad);
          } else {
            onEditOrder && onEditOrder(newLoad.id);
            setInitialLoadResponse(undefined);
            onClose && onClose('', newLoad.id);
          }

          setValidations([]);
        })
        .catch((error) => {
          console.error(error.message);
          logRocketTrack('Load build', {
            action: 'Duplicate load',
            severity: 'medium',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: error.data.message || 'Failed at duplicating load, please try again',
            variant: 'error'
          });
        })
        .finally(() => {
          setLoading(false);
          setIsDisable(false);
          setCreateTripFromOrder(false);
        });
    }
  };

  const handleEditLoad = async (openInvoice?: boolean, ignoreAndProceed = false): Promise<void> => {
    setLoading(true);

    // putting in state here because of warning helper functions... I'd put all in state vs passing variable "openInvoice" too but I couldn't get the state logic right so that it was set correctly before the rest of handle edit... feel free to cleanup if you're looking at this - LR
    if (openInvoice) {
      setOpenEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper(true);
    }
    const loadData: UpsertOrder = {
      status: data.status ? data.status : ORDER_STATUS.NOT_STARTED,
      rate: data.rate || 0,
      is_brokered_order: data.is_brokered_order,
      payable_amount: data.payable_amount,
      tz_usage: data.timezone_usage || TZ_USAGE.LOCAL,
      primary_client: data.selected_primary_client || CLIENT_TYPE.BROKER,
      customer_order_number: data.customer_order_number,
      dispatcher_id: data.selected_dispatcher || undefined,
      attachmentList: data.attachments,
      send_order_update: data.send_order_update,
      configurations:
        data.send_order_update && data.order_notification_settings
          ? {
              recipients: data.order_notification_settings?.recipients,
              order_status_events: data.order_notification_settings?.order_status_events,
              stop_status_events: data.order_notification_settings?.stop_status_events,
              stop_types: data.order_notification_settings?.stop_types
            }
          : null,

      order_type: data.order_type,
      trips: [],
      division_id: data.selected_division || userData?.defaultDivision.id,
      order_commodities: [],
      booking_type: enableLtl ? data.booking_type : BOOKING_TYPE.FTL,
      instructions: data.instructions,
      rate_table_id: data.rate_table_id || null
    };

    if (data.show_advanced_rates) {
      const loadRates: LoadRate[] = [];

      [...data.order_rates]
        .filter((lr) => !!lr.unit)
        .sort(
          (r1, r2) =>
            (r1.sort_order || data.order_rates.length) - (r2.sort_order || data.order_rates.length)
        )
        .forEach((lr, index) => {
          const unit = lr.unit || InvoiceServiceUnit.FLAT;

          loadRates.push({
            invoice_service_id: lr.qbo_service_id ? undefined : lr.invoice_service_id,
            qbo_service_id: lr.qbo_service_id ? lr.qbo_service_id : undefined,
            qbo_service_type: lr.qbo_service_type ? lr.qbo_service_type : undefined,
            service_name: lr.service_name,
            quantity: unit === InvoiceServiceUnit.FLAT ? 1 : lr.quantity || 0,
            rate: lr.rate || 0,
            unit,
            sort_order: index + 1,
            qbo_attributes: lr.qbo_attributes
          });
        });

      loadData.order_rates = loadRates;
      loadData.rate = undefined;
    } else {
      loadData.order_rates = LoadUtils.createDefaultAdvancedRate(
        data.rate,
        quickbooksEnabled,
        invoiceServicesData?.content,
        makeRateOptional
      );
      loadData.rate = undefined;
    }

    // load commodities check
    if (enableCommoditiesDatabase) {
      const commodities: OrderCommodities[] =
        data.new_order_commodities?.map((c) => {
          return mapCommodityListToOrderCommoditiesForSave(c);
        }) ?? [];
      loadData.order_commodities = commodities;
    } else {
      const newCommodities: OrderCommodities[] = [];
      data.order_commodities.forEach((commodity) => {
        if (commodity.description === '' && !commodity.quantity && !commodity.weight) {
          return;
        } else {
          const comm = {
            ...commodity,
            quantity: commodity.quantity || 0,
            weight: commodity.weight || 0
          };
          newCommodities.push(comm);
        }
      });
      if (newCommodities.length > 0) {
        loadData.order_commodities = newCommodities;
      }
    }

    data.selected_customer ? (loadData.broker_id = data.selected_customer) : null;
    data.selected_contact ? (loadData.customer_contact_id = data.selected_contact) : null;
    data.selected_dispatcher ? (loadData.dispatcher_id = data.selected_dispatcher) : null;

    const providerSegments = data.trips;
    const newLoadSegments: TripData[] = [];

    if (enableLtl) {
      const err = LoadUtils.validateStopIdentifiers(data.provider_stops);
      if (err) {
        setLoading(false);
        addNotification({
          message: err,
          variant: 'error'
        });
        return;
      }

      loadData.stops = data.provider_stops.map((s) => {
        const stop: TripStop = {
          location_id: s.provider_location?.id || null,
          order: s.order,
          sort_order: s.order,
          start_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          start_time: s.provider_time_from
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_from))
            : undefined,
          end_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
          end_time: s.provider_time_to
            ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_to))
            : undefined,
          notes: s.provider_include_location_notes ? s.notes : undefined,
          stop_type: (s.provider_stop_type?.id as STOP_TYPE) || undefined,
          stop_identifier:
            s.provider_stop_type?.id && s.provider_stop_identifier
              ? s.provider_stop_identifier
              : undefined,
          edi_stop_identifier: s.provider_edi_stop_identifier,
          contact: s.provider_include_location_notes ? s.contact : undefined,
          status: s.provider_stop_status || StopStatus.notStarted,
          is_in_time_overriden: s.provider_stop_in_time_overriden,
          is_out_time_overriden: s.provider_stop_out_time_overriden,
          id: s.id
        };

        if (s.provider_check_in_date && s.provider_check_in_time) {
          stop.in_time = DateUtils.getISODate(
            s.provider_check_in_date,
            s.provider_check_in_time ?? undefined
          );
        }

        if (s.provider_check_out_date && s.provider_check_out_time) {
          stop.out_time = DateUtils.getISODate(
            s.provider_check_out_date,
            s.provider_check_out_time ?? undefined
          );
        }
        return stop;
      });

      delete loadData['trips'];
    } else {
      for (const providerSegment of providerSegments) {
        // Build segment data
        const newSegment: TripData = {
          id: providerSegment.id,
          order: providerSegment.order,
          carrier_id: providerSegment.selected_carrier || undefined,
          instructions: providerSegment.instructions,
          trailer_id: providerSegment.selected_trailer || undefined,
          truck_id: providerSegment.selected_truck || undefined,
          empty_miles: providerSegment.empty_miles,
          estimated_mileage: providerSegment.estimated_mileage,
          pickup_number: providerSegment.pickup_number,
          status: providerSegment.status,
          stops: [],
          is_empty_miles_auto: providerSegment.is_empty_miles_auto,
          is_loaded_miles_auto: providerSegment.is_loaded_miles_auto,
          empty_mileage_source: providerSegment.empty_mileage_source,
          loaded_mileage_source: providerSegment.loaded_mileage_source
        };

        providerSegment.selected_trailer
          ? (newSegment.trailer_id = providerSegment.selected_trailer)
          : null;
        providerSegment.selected_truck
          ? (newSegment.truck_id = providerSegment.selected_truck)
          : null;
        providerSegment.selected_carrier
          ? (newSegment.carrier_id = providerSegment.selected_carrier)
          : null;
        providerSegment.selected_owner_operator
          ? (newSegment.owner_operator_id = providerSegment.selected_owner_operator)
          : null;

        // Save drivers to segment
        const driversToCreate = providerSegment.selected_drivers.filter((d) => !!d.id);

        driversToCreate.length > 0
          ? (newSegment.drivers = driversToCreate as TripDriverInfo[])
          : undefined;

        const err = LoadUtils.validateStopIdentifiers(providerSegment.provider_stops);
        if (err) {
          setLoading(false);
          addNotification({
            message: err,
            variant: 'error'
          });
          return;
        }

        // Build stops data
        newSegment.stops = providerSegment.provider_stops.map((s, index) => {
          const stop: TripStop = {
            location_id: s.provider_location?.id || null,
            order: s.order,
            sort_order: s.order,
            start_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
            start_time: s.provider_time_from
              ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_from))
              : undefined,
            end_date: s.provider_date ? DateUtils.getShortDate(s.provider_date) : '',
            end_time: s.provider_time_to
              ? DateUtils.get24HourFormat(DateUtils.getTime(s.provider_time_to))
              : undefined,
            notes: s.provider_include_location_notes ? s.notes : undefined,
            stop_type: (s.provider_stop_type?.id as STOP_TYPE) || undefined,
            stop_identifier:
              s.provider_stop_type?.id && s.provider_stop_identifier
                ? s.provider_stop_identifier
                : undefined,
            edi_stop_identifier: s.provider_edi_stop_identifier,
            contact: s.provider_include_location_notes ? s.contact : undefined,
            status: s.provider_stop_status || StopStatus.notStarted,
            is_in_time_overriden: s.provider_stop_in_time_overriden,
            is_out_time_overriden: s.provider_stop_out_time_overriden,
            id: s.id
          };

          if (s.provider_check_in_date && s.provider_check_in_time) {
            stop.in_time = DateUtils.getISODate(
              s.provider_check_in_date,
              s.provider_check_in_time ?? undefined
            );
          }

          if (s.provider_check_out_date && s.provider_check_out_time) {
            stop.out_time = DateUtils.getISODate(
              s.provider_check_out_date,
              s.provider_check_out_time ?? undefined
            );
          }
          return stop;
        });

        if (
          providerSegment.stops?.length !== newSegment.stops.length &&
          !isAutoCalculateMileageDisabled
        ) {
          // Reset est. mileage to force recalculation
          newSegment.estimated_mileage = null;
        }

        newLoadSegments.push(newSegment);
      }

      // Add segments to load data
      loadData.trips = newLoadSegments;
    }

    loadData.documents = data.existing_documents?.map((doc) => doc.id);

    let driversDifference: INotifyDriverWithFullSegment[] = [];
    let driversUnassigned: INotifyDriverWithFullSegment[] = [];

    // Save segment
    setIsDisable(true);

    let validationsResponse: ValidationsResponse | null = null;
    let hasValidations = false;
    if (enableWarningHelper && !ignoreAndProceed) {
      try {
        validationsResponse = await validateDispatch(
          DispatchHelperUtils.getValidateDispatchPayload(loadData, enableLtl, loadId)
        ).unwrap();
      } catch (error) {
        console.error(error);
      }
    }

    if (validationsResponse) {
      hasValidations = validationsResponse.data.length > 0;

      // check if there are new validations
      const hasNewValidations = !_.isEqual(validationsResponse.data, validations);

      if (!hasNewValidations) {
        handleIgnoreAndProceed(openInvoice);

        return;
      }

      if (!hasValidations) {
        handleIgnoreAndProceed(openInvoice);

        return;
      }

      setOpenWarningHelper(hasValidations);
      setValidations(validationsResponse.data);
    }

    if (!hasValidations || ignoreAndProceed || !enableWarningHelper) {
      updateOrder({
        id: loadId,
        data: loadData,
        searchTripParams: {
          filters: { ...tripsFilterParams, includeArchived: matchesMobileOrTablet }
        },
        isLtl: enableLtl
      })
        .unwrap()
        .then(async (load) => {
          setLoadIdForNotifyDrivers(load.id);
          driversDifference = findDiffDriversEditLoad(
            originalDriversData,
            load.trips,
            load.readable_id
          );
          driversUnassigned = findUnassignedDrivers(
            originalDriversData,
            load.trips,
            load.readable_id
          );

          if (documents && documents?.length > 0) {
            const docsWithOrder = documents.map((d) => {
              return { ...d, orderId: load.id };
            });
            return await Promise.all(
              docsWithOrder.map((doc) =>
                uploadOrderTripDocument({
                  document: doc,
                  reviewed: true
                })
                  .unwrap()
                  .catch((error) => {
                    logRocketTrack('Upload', {
                      action: 'Upload document on edit load',
                      severity: 'high',
                      tenant_id: userData?.tenant?.id,
                      company_name: userData?.tenant?.company_name,
                      email: userData?.email_address,
                      user_first_name: userData?.first_name,
                      user_last_name: userData?.last_name
                    });
                    console.error('Error upload document', error);
                    addNotification({
                      message: 'Load edited, but documents failed to upload. Please re-upload.',
                      variant: 'warning'
                    });
                  })
              )
            );
          }

          setValidations([]);
        })
        .then(async () => {
          const documentToUpdate = data.existing_documents?.filter(
            (doc) => doc.updated_at && parseInt(doc.updated_at) <= parseInt(Date.now().toString())
          );

          if (documentToUpdate && documentToUpdate?.length > 0) {
            // shape to format needed to update
            const documentParams = documentToUpdate.map((doc) => {
              const obj = {
                id: doc.id,
                name: doc.attachment.name,
                type: doc.type,
                tripId: doc.trip_id,
                orderId: doc.order_id,
                visibility: doc.visibility
              };

              return obj;
            });

            return updateManyDocuments(documentParams)
              .unwrap()
              .catch((error) => {
                console.error('Error updating documents', error);
                addNotification({
                  message: 'Load edited, but documents to update failed. Please try again.',
                  variant: 'warning'
                });
              });
          }

          setValidations([]);
        })
        .then(() => {
          if (driversDifference.length > 0) {
            const updatedArrayDrivers: INotifyDriverWithFullSegment[] = [];

            driversDifference.forEach((segment) => {
              const selectedDriver = {
                driverId: segment.driverId,
                fullSegment: segment.fullSegment,
                driverIndex: segment.driverIndex,
                isForExistingLoadUpdate: segment.isForExistingLoadUpdate,
                deleted_trip_index: segment.deleted_trip_index
              };
              updatedArrayDrivers.push(selectedDriver);
            });

            setDriversToNotify(updatedArrayDrivers);
          }

          if (driversUnassigned.length > 0) {
            setUnassignedDriversToNotify(driversUnassigned);
          }

          if (
            !enableSkipDispatchModal &&
            (driversDifference.length > 0 || driversUnassigned.length > 0) &&
            !(hasRateChanged && loadHasInvoiceSent)
          ) {
            setOpenNotify(true);
          } else {
            onEditOrder && onEditOrder(loadId);
            handleClose(openInvoice);
          }

          setValidations([]);
        })
        .catch((err) => {
          console.error(err);
          addNotification({
            message:
              err.data.message || "Error: Couldn't update order. Contact support@truckbase.com.",
            variant: 'error'
          });
        })
        .finally(() => {
          setLoading(false);
          setIsDisable(false);
        });
    }
  };

  const handleSave = (): void => {
    if (duplicate_load) {
      handleDuplicateLoad();
      return;
    }

    if (
      hasRateChanged &&
      loadHasInvoiceSent &&
      ((quickbooksEnabled && loadInvoiceSource === InvoiceSources.QBO) ||
        loadInvoiceSource === InvoiceSources.TB)
    ) {
      userData?.role === USER_ROLE.DISPATCHER
        ? addNotification({
            message:
              'You cannot edit rates on orders with existing invoices. Please contact your admin for assistance.',
            variant: 'error'
          })
        : setOpenInvoiceWarning(true);
    } else {
      handleEditLoad();
    }
  };

  const closeNotify = (): void => {
    setOpenNotify(false);
    setDriversToNotify([]);
    setUnassignedDriversToNotify([]);
    setOriginalDriversData([]);
    // close edit modal too
    onEditOrder && onEditOrder();
    handleClose();
  };

  const handleFormSubmit = (): void => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handleQboAlertAction = (): void => {
    if (!!data?.invoice) {
      setIsQboLearnMoreModalOpen(true);
    } else {
      if (anyDriverPayExists) {
        dispatch(
          updateDriverPayConfirmationModal({
            open: true,
            action: () =>
              dispatch(
                resetAdvancedRates({
                  services: invoiceServicesData.content
                })
              )
          })
        );
      } else {
        dispatch(
          resetAdvancedRates({
            services: invoiceServicesData.content
          })
        );
      }
    }
  };

  const handleCloseWarningHelper = (): void => {
    setIsDisable(false);
    setLoading(false);
    setOpenWarningHelper(false);
  };

  const handleIgnoreAndProceed = (openInvoiceOnEdit?: boolean): void => {
    setOpenWarningHelper(false);

    if (duplicate_load) {
      handleDuplicateLoad(true);
      return;
    }

    // using this openEdit...AfterDispatch... var from state here because of warning helper functions... I'd put all in state vs passing variable "openInvoice" too but I couldn't get the state logic right so that it was set correctly before the rest of handle edit... feel free to cleanup if you're looking at this - LR
    handleEditLoad(
      openInvoiceOnEdit || openEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper,
      true
    );
  };

  const consolidateInTripFromOrder = (order: Order): void => {
    if (order) {
      try {
        const allStops: SliceStop[] = [];
        const allCommodities: TripCommodities[] = [];

        const orderData = {
          id: order.id,
          readable_id: order.readable_id,
          customer_number: order.customer_order_number || '',
          customerName: order.raw_customer?.name || ''
        };

        const { stops, commodities } = formatStopsAndCommodities(
          order.stops || [],
          order.order_commodities || [],
          orderData
        );

        allStops.push(...stops);
        // Set order and stops context first
        dispatch(setOrdersForConsolidation([{ id: order.id, readable_id: order.readable_id }]));
        dispatch(addOrderStopsToTrip(allStops));

        // Now add commodities (must be after stops and order context)
        if (enableCommoditiesDatabase) {
          const mappedCommodities = commodities.map((commodity) =>
            mapPrefilledOrderCommodityToCommodityList(commodity)
          );
          dispatch(addNewOrderCommoditiesToTrip(mappedCommodities));
        } else {
          allCommodities.push(...commodities);
          dispatch(addOrderCommoditiesToTrip(allCommodities));
        }

        dispatch(openCreateTrip(true));
      } catch (error) {
        console.error(error);
        addNotification({
          variant: 'error',
          message: 'Error consolidating order into trip.'
        });
      }
    }
  };

  const checkIfRateTableIsActive = (
    rateTable: RateTableAttributes | null | undefined,
    pickupDate?: string | null
  ): boolean => {
    if (!rateTable || !pickupDate) {
      return true;
    }

    const isActive = DateUtils.isDateActive(
      rateTable.start_date,
      rateTable.end_date ? rateTable.end_date : null,
      pickupDate
    );

    return isActive;
  };

  // #region effects
  useEffect(() => {
    if (!initialLoadResponse || !open) {
      return;
    }

    if (!initialLoadResponse.order_rates || initialLoadResponse.order_rates.length <= 0) return;
    const { initialRates, orderRates } = getRatesFromOrderState(
      initialLoadResponse,
      quickbooksEnabled
    );

    dispatch(updateLoadRates({ rates: orderRates, resetDriverPayOverrides: false }));
    dispatch(updateInitialLoadRates(initialRates));
  }, [quickbooksEnabled]);

  useEffect(() => {
    if (!componentReady) {
      return;
    }
    if (!initialLoadResponse) {
      // When dialog is closed we must reset the rates
      // because the rates are not reloaded when the dialog is opened again
      dispatch(updateLoadRates({ rates: [], resetDriverPayOverrides: false }));
      dispatch(updateInitialLoadRates([getEmptyLoadRateServiceRow()]));
      return;
    }
  }, [initialLoadResponse, loading]);

  useEffect(() => {
    const quickbooksEnabled = !!divisionAccountingSettings?.division_qbo_credentials?.id;
    dispatch(updateQuickbooksEnabled(quickbooksEnabled));
  }, [divisionAccountingSettings?.division_qbo_credentials?.id]);

  useEffect(() => {
    if (
      open &&
      data.qbo_attributes?.division_qbo_credentials_id !=
        divisionAccountingSettings?.division_qbo_credentials?.id
    ) {
      dispatch(updateLoadRatesDisabled(true));
    } else {
      dispatch(updateLoadRatesDisabled(false));
    }
  }, [
    data.qbo_attributes?.division_qbo_credentials_id,
    divisionAccountingSettings?.division_qbo_credentials?.id
  ]);

  useEffect(() => {
    if (!open) {
      resetState();
      dispatch(setLoadDetailReload(true));
      return;
    }

    (async (): Promise<void> => {
      setOpenEditInvoiceOnEditOrderSaveAfterDispatchWarningHelper(false);
      await getLoad();
      setLoading(false);
      setComponentReady(true);
    })();
  }, [open]);

  useEffect(() => {
    if (!!data?.invoice && data?.load_rates_disabled) {
      setIsMainActionDisable(true);
    }
  }, [data?.invoice, data?.load_rates_disabled]);

  const hasRateChanged = useMemo(() => {
    if (data.show_advanced_rates) {
      // Check if total rate has changed
      const total = data.order_rates.reduce((acc, row) => {
        return acc + Number(row.rate) * Number(row.quantity);
      }, 0);

      // Check if any QBO class refs have changed
      const qboClassChanged =
        quickbooksEnabled &&
        data.order_rates.some((currentRate, index) => {
          const originalOrderRate = originalOrderRates[index];
          return (
            currentRate.qbo_attributes?.qbo_class_ref?.value !==
            originalOrderRate?.qbo_attributes?.qbo_class_ref?.value
          );
        });

      return total !== originalRate || qboClassChanged;
    } else {
      return data.rate !== originalRate;
    }
  }, [data.order_rates, data.rate, data.show_advanced_rates, originalRate]);

  const qboAlertMessage = useMemo<string>((): string => {
    if (!!data?.invoice) {
      return !!data?.qbo_attributes?.division_qbo_credentials_id
        ? 'The QuickBooks account linked to this load has been disconnected. Modifications to services, rates, and invoices are restricted.'
        : 'This load was created using Truckbase services, and you are now connected to QuickBooks. Modifications to services, rates, and invoices are restricted.';
    } else {
      return !!data?.qbo_attributes?.division_qbo_credentials_id
        ? 'The QuickBooks account previously linked to this load has been disconnected. Please update the rate information manually.'
        : 'This load was created using Truckbase services, and you are now connected to QuickBooks. Please update the rate information manually.';
    }
  }, [data?.invoice, data?.qbo_attributes?.division_qbo_credentials_id]);

  const qboAlertActionLabel = useMemo<string>((): string => {
    return !!data?.invoice ? 'LEARN MORE' : 'CLEAR RATES';
  }, [data?.invoice]);

  const bottomActions = useMemo(() => {
    const actions = [
      <TwButton
        key="1"
        type={TwButtonType.secondary}
        label="Cancel"
        action={handleClose}
        disabled={isDisable}
      />
    ];

    if (enableLtl && duplicate_load) {
      actions.push(
        <TwButton
          key="3"
          type={TwButtonType.secondary}
          label={'Create order & trip'}
          action={(): void => {
            setCreateTripFromOrder(true);
            handleFormSubmit();
          }}
          disabled={isDisable}
          sx={{ marginRight: '1rem' }}
        />
      );
    }

    actions.push(
      <TwButton
        key="2"
        type={TwButtonType.primary}
        label={duplicate_load ? 'Create' : 'Save'}
        action={handleFormSubmit}
        disabled={isDisable}
      />
    );

    return actions;
  }, [isDisable, enableLtl, duplicate_load, handleClose, handleFormSubmit]);

  return (
    <>
      <TwDialog
        title={`${duplicate_load ? 'Duplicate' : 'Edit'} Order #${loadNumber}`}
        fullScreen
        open={open}
        onClickClose={handleClose}
        actions={enableLtl && duplicate_load ? undefined : bottomActions}
        fullWidthAction={
          enableLtl &&
          duplicate_load && (
            <Box display="flex" justifyContent="space-between" width="100%">
              <Box>{bottomActions[0]}</Box>
              <Box>{bottomActions.slice(1)}</Box>
            </Box>
          )
        }
      >
        {!!data?.load_rates_disabled && !duplicate_load && (
          <TwAlert
            message={qboAlertMessage}
            severity="warning"
            icon={<AlertIcon />}
            sx={[
              {
                '& .MuiAlert-icon': {
                  padding: '8px 0 0px'
                },
                '& .MuiAlert-action': {
                  padding: '0px 0px'
                }
              },
              {
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                marginBottom: '30px'
              }
            ]}
            action={
              <TwButton
                type={TwButtonType.text}
                label={qboAlertActionLabel}
                action={handleQboAlertAction}
              />
            }
          />
        )}

        {/* Spinner indicator to prevent render component until actions needed before render are completed*/}
        {!componentReady && (
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: 10
            }}
            open={true}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        {/* Spinner indicator to prevent render component until actions needed before render are completed end*/}

        {componentReady && (
          <>
            <Grid container spacing={6}>
              {matchesAboveLarge && (
                <QuickMenuItems
                  isOrderInfoVisible={isOrderInfoVisible}
                  isRouteInfoVisible={isRouteInfoVisible}
                  isDispatchInfoVisible={isDispatchInfoVisible}
                  isAttachmentsVisible={isAttachmentsVisible}
                  tripsVisibilities={tripsVisibilities}
                  orderInfoRef={orderInfoRef}
                  routeInfoRef={routeInfoRef}
                  dispatchRef={dispatchRef}
                  attachmentsRef={attachmentsInfoRef}
                  tripsRef={tripsRef}
                  entity={EntityType.ORDER}
                />
              )}
              <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
                <TwLoadForm onSubmit={handleSave} formRef={formRef}>
                  <DataContainer ref={containerRef}>
                    <OrderInfo
                      ref={orderInfoRef}
                      dataContainerSx={{
                        width: '100% !important',
                        maxWidth: `${858 / 16}rem`,
                        marginBottom: '3rem'
                      }}
                      divisionId={data.selected_division}
                      loading={loading}
                      divisionEditable={duplicate_load || !data.invoice}
                      initialPickupDate={pickupDate}
                      showRateTables={showRateTable}
                    />
                    <Route
                      ref={routeInfoRef}
                      routeContainerSx={{
                        width: '100% !important',
                        maxWidth: `${858 / 16}rem`,
                        marginBottom: '3rem',
                        marginTop: '3rem'
                      }}
                    />
                    {!enableLtl && (
                      <>
                        {data.order_type === LOAD_TYPE.SIMPLE ? (
                          <DispatchInfo
                            ref={dispatchRef}
                            dispatchContainerSx={{
                              width: '100% !important',
                              maxWidth: `${858 / 16}rem`,
                              marginBottom: '3rem',
                              marginTop: '3rem'
                            }}
                            loading={loading}
                            entity={EntityType.ORDER}
                          />
                        ) : (
                          data.trips.map((segment, index) => {
                            return (
                              <TwDispatchTripFromOrder
                                key={`split-load-segment-${segment.order}-trip-${index}`}
                                ref={tripsRef.current[index]}
                                segment={segment}
                                totalLoadedMiles={data.estimated_mileage}
                                loading={loading}
                                editMode
                              />
                            );
                          })
                        )}
                      </>
                    )}

                    <AttachmentsInfo
                      ref={attachmentsInfoRef}
                      additionalContainerSx={{
                        width: '100% !important',
                        maxWidth: `${858 / 16}rem`,
                        marginBottom: '3rem'
                      }}
                      entity={EntityType.ORDER}
                    />
                  </DataContainer>
                </TwLoadForm>
              </Grid>
              {matchesAboveLarge && <Grid item lg={3} xl={4}></Grid>}
            </Grid>
            <Backdrop
              sx={{
                color: '#fff',
                zIndex: 10
              }}
              open={loading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </>
        )}
      </TwDialog>
      <TwNotifyDriver
        open={openNotify}
        driversToNotify={driversToNotify}
        unassignedDriversToNotify={unassignedDriversToNotify}
        orderId={loadIdForNotifyDrivers}
        onClose={closeNotify}
        isSplitLoad={data.order_type === LOAD_TYPE.SEGMENT}
      />
      <TwEditLoadInvoiceWarning
        open={openInvoiceWarning}
        close={(): void => {
          setOpenInvoiceWarning(false);
        }}
        saveLoad={(): void => {
          setOpenInvoiceWarning(false);
          handleEditLoad(false);
        }}
        saveAndUpdateInvoice={(): void => {
          setOpenInvoiceWarning(false);
          handleEditLoad(true);
        }}
      />
      <TwInfoModal
        open={isQboLearnMoreModalOpen}
        title="Why I can't edit rates on this load?"
        body={
          <Stack direction="column" spacing="5">
            <Typography
              variant="body2"
              fontWeight="400"
              fontSize="14px"
              color="#6B7280"
              marginBottom="20px"
            >
              The QuickBooks account linked to this load has been disconnected. Modifications to
              services, rates, and invoices are restricted.
            </Typography>
            <Typography variant="body2" fontWeight="400" fontSize="14px" color="#6B7280">
              To edit this load, first delete the existing invoice associated to this load. Once
              deleted, you will be able to update load rates and resend the invoice using the
              current invoicing method.
            </Typography>
          </Stack>
        }
        onClickClose={(): void => {
          setIsQboLearnMoreModalOpen(false);
        }}
      />

      {enableWarningHelper && (
        <TwWarningHelper
          validations={validations}
          open={openWarningHelper}
          handleClose={handleCloseWarningHelper}
          onContinue={handleIgnoreAndProceed}
        />
      )}
      {driver_pay_confirmation_modal.open && !enableLtl && (
        <TwDriverPayConfirmationModal open={driver_pay_confirmation_modal.open} />
      )}

      {show_rates_warning_ltl.open && enableLtl && (
        <TwLTLRatesWarning open={show_rates_warning_ltl.open} />
      )}
    </>
  );
};

export default EditDuplicateOrder;
